// TMOS Plan Objective Service
// Date: January 13, 2026
// Purpose: Frontend service for plan objective management

import api from './api';
import { PlanObjective, CreatePlanObjectiveRequest, UpdatePlanObjectiveRequest } from '../types/plan';
import { ObjectiveProgress } from '../types/businessPerformance';

export interface ObjectiveFilters {
  status?: 'ACTIVE' | 'COMPLETED' | 'ON_HOLD' | 'CANCELLED';
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  kpi_id?: string;
}

export const objectiveService = {
  /**
   * Get objectives by plan
   */
  async getObjectivesByPlan(planId: string): Promise<PlanObjective[]> {
    const response = await api.get<{ success: boolean; data: PlanObjective[]; total: number }>(
      `/objectives/plan/${planId}`
    );
    return response.data.data;
  },

  /**
   * Get objective by ID
   */
  async getObjectiveById(objectiveId: string): Promise<PlanObjective> {
    const response = await api.get<{ success: boolean; data: PlanObjective }>(
      `/objectives/${objectiveId}`
    );
    return response.data.data;
  },

  /**
   * Create new objective
   */
  async createObjective(planId: string, objectiveData: CreatePlanObjectiveRequest): Promise<PlanObjective> {
    const response = await api.post<{ success: boolean; message: string; data: PlanObjective }>(
      `/objectives/plan/${planId}`,
      objectiveData
    );
    return response.data.data;
  },

  /**
   * Update objective
   */
  async updateObjective(objectiveId: string, objectiveData: UpdatePlanObjectiveRequest): Promise<PlanObjective> {
    const response = await api.put<{ success: boolean; message: string; data: PlanObjective }>(
      `/objectives/${objectiveId}`,
      objectiveData
    );
    return response.data.data;
  },

  /**
   * Delete objective
   */
  async deleteObjective(objectiveId: string): Promise<void> {
    await api.delete<{ success: boolean; message: string }>(`/objectives/${objectiveId}`);
  },

  /**
   * Get objective progress
   */
  async getObjectiveProgress(objectiveId: string, planId?: string): Promise<ObjectiveProgress> {
    const params = planId ? `?plan_id=${planId}` : '';
    const response = await api.get<{ success: boolean; data: ObjectiveProgress }>(
      `/objectives/${objectiveId}/progress${params}`
    );
    return response.data.data;
  },

  /**
   * Link objective to plan
   */
  async linkObjectiveToPlan(planId: string, objectiveId: string, relationshipType: 'PRIMARY' | 'SECONDARY' = 'PRIMARY'): Promise<void> {
    await api.post<{ success: boolean; message: string }>(
      `/objectives/plan/${planId}/link/${objectiveId}`,
      { relationship_type: relationshipType }
    );
  },

  /**
   * Unlink objective from plan
   */
  async unlinkObjectiveFromPlan(planId: string, objectiveId: string): Promise<void> {
    await api.delete<{ success: boolean; message: string }>(
      `/objectives/plan/${planId}/unlink/${objectiveId}`
    );
  }
};

